import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { Spin, Empty } from "antd";
import { LockOutlined, PictureOutlined, FileImageOutlined } from "@ant-design/icons";

import { Post, getPostsBySubcategory, getPostData, getImageList, getGrossImageList, isPostDetailBlocked } from "../api/postAPI";
import { MainColor, SubColor } from "../style/styleValue";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 10;
const ROWH = 168; // 연속 흐름의 행 높이(px) — 모든 타일/라벨 동일 높이
const GAP = 8; // 타일 간격(px)
const photoUrl = (path: string) => `${BACKEND}/api/photo/${path}`;
const grossUrl = (path: string) => `${BACKEND}/api/gross-photo/file/${path}`;

type MediaKind = "slide" | "image" | "gross";

interface MediaThumb {
  key: string;
  url: string;
  kind: MediaKind;
  label?: string;
}

interface PostMedia {
  thumbs: MediaThumb[];
  slideCount: number;
  imageCount: number;
  loading: boolean;
  locked: boolean;
}

const emptyMedia: PostMedia = { thumbs: [], slideCount: 0, imageCount: 0, loading: true, locked: false };

const GalleryStrip = () => {
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const [searchParams] = useSearchParams();

  const [posts, setPosts] = useState<Post[]>([]);
  const [mediaByPost, setMediaByPost] = useState<Record<number, PostMedia>>({});
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const pageRef = useRef(1);
  const fetchingRef = useRef(false);
  const cancelledRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const organName = searchParams.get("name") || "갤러리";

  const loadNextPage = useCallback(async () => {
    if (!categoryId || fetchingRef.current || !hasMore) return;
    fetchingRef.current = true;

    const page = pageRef.current;
    if (page === 1) setInitialLoading(true);
    else setLoadingMore(true);

    try {
      const res: Post[] = await getPostsBySubcategory(
        page,
        PAGE_SIZE,
        +categoryId,
        "modifiedAt",
        "DESC",
        null,
        null
      );
      if (cancelledRef.current) return;

      const batch = Array.isArray(res) ? res : [];
      setPosts((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...batch.filter((p) => !seen.has(p.id))];
      });
      setMediaByPost((prev) => ({
        ...prev,
        ...Object.fromEntries(batch.map((p) => [p.id, { ...emptyMedia }])),
      }));

      if (batch.length < PAGE_SIZE) setHasMore(false);
      pageRef.current = page + 1;

      batch.forEach(async (post) => {
        const media = await fetchPostMedia(post);
        if (!cancelledRef.current) {
          setMediaByPost((prev) => ({ ...prev, [post.id]: media }));
        }
      });
    } catch (e) {
      console.error("GalleryStrip load error:", e);
      if (!cancelledRef.current) setHasMore(false);
    } finally {
      if (!cancelledRef.current) {
        setInitialLoading(false);
        setLoadingMore(false);
      }
      fetchingRef.current = false;
    }
  }, [categoryId, hasMore]);

  useEffect(() => {
    cancelledRef.current = false;
    pageRef.current = 1;
    setPosts([]);
    setMediaByPost({});
    setHasMore(true);
    setInitialLoading(true);
    loadNextPage();

    return () => {
      cancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadNextPage();
      },
      { rootMargin: "300px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadNextPage, initialLoading, posts.length, hasMore]);

  const goToPost = (post: Post) => {
    const params = new URLSearchParams();
    params.set("title", post.title);
    params.set("from", "gallery");
    if (organName !== "갤러리") params.set("name", organName);
    if (categoryId) params.set("categoryId", categoryId);
    navigate(`/post/${post.id}?${params.toString()}`);
  };

  const visiblePosts = posts.filter((post) => {
    const m = mediaByPost[post.id];
    return !!m && !m.loading && m.thumbs.length > 0;
  });
  const anyPending = posts.some((post) => {
    const m = mediaByPost[post.id];
    return !m || m.loading;
  });

  if (initialLoading) {
    return (
      <CenterState>
        <Spin size="large" />
      </CenterState>
    );
  }

  return (
    <Container>
      <PageHeader>
        <Eyebrow>ATLAS GALLERY</Eyebrow>
        <Title>{organName}</Title>
        <Count>{visiblePosts.length}{hasMore || anyPending ? "+" : ""} cases</Count>
      </PageHeader>

      {visiblePosts.length === 0 ? (
        anyPending || loadingMore || hasMore ? (
          <CenterState>
            <Spin size="large" />
          </CenterState>
        ) : (
          <CenterState>
            <Empty description="표시할 갤러리가 없습니다." />
          </CenterState>
        )
      ) : (
        // 모든 Post의 [라벨 + 썸네일]을 하나의 연속 흐름으로 배치한다.
        // - 타일은 flex-grow 로 각 줄의 폭을 빈틈없이 채우고, 넘치면 다음 줄로 이어진다.
        // - Post 경계는 각 Post 앞의 라벨 칩으로만 표시(줄바꿈을 강제하지 않아 빈 공간이 없다).
        // - 여러 Post가 한 줄에 함께 이어질 수 있고, 한 Post가 여러 줄에 걸칠 수도 있다.
        <Wall>
          {visiblePosts.map((post) => {
            const media = mediaByPost[post.id] ?? emptyMedia;
            return (
              <FlowGroup key={post.id}>
                <LabelChip onClick={() => goToPost(post)}>
                  <LabelTitle>
                    {post.isSecret && <LockOutlined style={{ marginRight: 5, color: "#8c8c8c" }} />}
                    {post.title || "제목 없음"}
                  </LabelTitle>
                  <LabelSub>
                    <LabelWriter>{post.writer || "작성자 미상"}</LabelWriter>
                    <LabelCounts>
                      {media.slideCount > 0 && (
                        <span>
                          <FileImageOutlined /> {media.slideCount}
                        </span>
                      )}
                      {media.imageCount > 0 && (
                        <span>
                          <PictureOutlined /> {media.imageCount}
                        </span>
                      )}
                    </LabelCounts>
                  </LabelSub>
                </LabelChip>

                {media.thumbs.map((t) => (
                  <Tile key={t.key} onClick={() => goToPost(post)} title={t.label}>
                    <KindTag $kind={t.kind}>
                      {t.kind === "slide" ? "S" : t.kind === "gross" ? "G" : "I"}
                    </KindTag>
                    <img
                      src={t.url}
                      alt={t.label || ""}
                      loading="lazy"
                      onError={(e) => {
                        const el = e.currentTarget;
                        el.style.display = "none";
                        (el.parentElement as HTMLElement).classList.add("broken");
                      }}
                    />
                  </Tile>
                ))}
              </FlowGroup>
            );
          })}
        </Wall>
      )}

      {posts.length > 0 && (
        <ScrollFooter ref={sentinelRef}>
          {loadingMore && <Spin />}
          {!hasMore && !loadingMore && <EndText>모든 게시글을 불러왔습니다.</EndText>}
        </ScrollFooter>
      )}
    </Container>
  );
};

const fetchPostMedia = async (post: Post): Promise<PostMedia> => {
  if (post.isSecret) {
    return { thumbs: [], slideCount: 0, imageCount: 0, loading: false, locked: true };
  }

  try {
    const [detail, images, grossImages] = await Promise.all([
      getPostData(post.id),
      getImageList(post.id),
      getGrossImageList(post.id),
    ]);

    if (isPostDetailBlocked(detail)) {
      return { thumbs: [], slideCount: 0, imageCount: 0, loading: false, locked: true };
    }

    const slides: any[] = detail?.slides ?? post.slides ?? [];
    const photos: any[] = Array.isArray(images) ? images : [];
    const gross: any[] = Array.isArray(grossImages) ? grossImages : [];

    const thumbs: MediaThumb[] = [
      ...slides
        .filter((s) => s?.thumbnail)
        .map((s, i) => ({ key: `s-${s.slideId ?? i}`, url: photoUrl(s.thumbnail), kind: "slide" as const, label: s.slidename })),
      ...photos
        .filter((p) => p?.pathFileName)
        .map((p, i) => ({ key: `i-${p.photoId ?? i}`, url: photoUrl(p.pathFileName), kind: "image" as const, label: p.imageName })),
      ...gross
        .filter((g) => g?.pathFileName)
        .map((g, i) => ({ key: `g-${g.grossPhotoId ?? i}`, url: grossUrl(g.pathFileName), kind: "gross" as const, label: g.caption })),
    ];

    return {
      thumbs,
      slideCount: slides.length,
      imageCount: photos.length + gross.length,
      loading: false,
      locked: false,
    };
  } catch (e) {
    console.error(`post ${post.id} media load error:`, e);
    return { thumbs: [], slideCount: 0, imageCount: 0, loading: false, locked: false };
  }
};

const Container = styled.div`
  width: 100%;
  max-width: 2000px;
  margin: 0 auto;
  padding: 2.5rem 4rem 5rem;

  @media (max-width: 768px) {
    padding: 1.5rem 1.25rem 3rem;
  }
`;

const CenterState = styled.div`
  min-height: 50vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding-bottom: 2rem;
  margin-bottom: 2.5rem;
  border-bottom: 1px solid #eef1f5;
`;

const Eyebrow = styled.span`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  color: ${SubColor};
  margin-bottom: 10px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 2.4rem;
  font-weight: 700;
  color: ${MainColor};
  letter-spacing: -0.5px;
`;

const Count = styled.span`
  margin-top: 10px;
  font-size: 0.95rem;
  color: #999;
`;

// 연속 흐름 컨테이너: 라벨/타일이 좌→우로 채워지다 폭을 넘으면 다음 줄로 이어진다.
const Wall = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${GAP}px;
`;

// JSX상 Post 단위로 묶되, display:contents 로 자기 박스를 만들지 않아
// 라벨+타일이 Wall 의 직접 flex 아이템처럼 연속으로 흐른다.
const FlowGroup = styled.div`
  display: contents;
`;

// Post 경계를 알리는 라벨 칩(줄바꿈을 강제하지 않음). 타일과 동일 높이로 흐름에 참여.
const LabelChip = styled.div`
  position: relative;
  flex: 1 1 ${ROWH * 1.5}px;
  min-width: ${ROWH * 1.3}px;
  height: ${ROWH}px;
  box-sizing: border-box;
  padding: 14px 16px;
  border-radius: 8px;
  background: ${MainColor};
  color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  overflow: hidden;

  &:hover {
    filter: brightness(1.08);
  }
`;

const LabelTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.4;
  color: #fff;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const LabelSub = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const LabelWriter = styled.span`
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.85);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LabelCounts = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.72rem;

  span {
    display: inline-flex;
    align-items: center;
    gap: 3px;
  }
  .anticon {
    font-size: 11px;
  }
`;

const Tile = styled.div`
  position: relative;
  /* flex-grow 로 각 줄의 남는 폭을 나눠가져 빈틈없이 채운다. 높이는 고정(ROWH). */
  flex: 1 1 ${ROWH * 1.1}px;
  height: ${ROWH}px;
  border-radius: 8px;
  overflow: hidden;
  background: #f0f2f5;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.35s ease;
  }

  &:hover img {
    transform: scale(1.06);
  }

  &.broken::after {
    content: "";
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(45deg, #f0f2f5, #f0f2f5 10px, #e8ebf0 10px, #e8ebf0 20px);
  }
`;

const KindTag = styled.span<{ $kind: MediaKind }>`
  position: absolute;
  top: 6px;
  left: 6px;
  z-index: 1;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #fff;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${({ $kind }) =>
    $kind === "slide"
      ? "rgba(23, 55, 95, 0.85)"
      : $kind === "gross"
      ? "rgba(180, 83, 9, 0.85)"
      : "rgba(48, 104, 164, 0.85)"};
`;

const ScrollFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60px;
  padding: 2rem 0 0;
`;

const EndText = styled.span`
  color: #b5bcc7;
  font-size: 0.9rem;
`;

export default GalleryStrip;
